import { Badge, Card, IconTile } from "@design";
import { Form, Sparkles } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";
import { useAreaCrumbs } from "@/components/areaCrumbs";
import { PageBreadcrumbs } from "@/components/PageBreadcrumbs";
import { Text } from "@/components/Text";
import { AgentForm } from "./AgentForm";

/**
 * /build/agents/new — create page (no modal). A choice screen first: build
 * with AI (the Build with AI chat, set up for agents) or start from the form
 * (`/build/agents/new/form`).
 */
export function NewAgentPage() {
  const { t } = useTranslation(["build", "agents"]);
  const areas = useAreaCrumbs();
  const navigate = useNavigate();
  const { mode } = useParams();

  return (
    <div className="flex flex-col">
      <PageBreadcrumbs
        trail={[areas.build, { label: t("agents:title"), to: "/build/agents" }]}
        current={t("agents:create.title")}
      />
      <div className="mx-auto flex w-full max-w-3xl flex-col gap-6 px-4 py-6 md:px-8">
        {mode === "form" ? (
          <AgentForm />
        ) : (
          <>
            <Text variant="h3">{t("agents:create.how")}</Text>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <ChoiceCard
                icon={<Sparkles size={18} />}
                title={t("agents:create.withAi.title")}
                body={t("agents:create.withAi.body")}
                badge={t("build:withAi.badge")}
                onClick={() => navigate("/build/with-ai?entity=agent")}
              />
              <ChoiceCard
                icon={<Form size={18} />}
                title={t("agents:create.fromScratch.title")}
                body={t("agents:create.fromScratch.body")}
                onClick={() => navigate("/build/agents/new/form")}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function ChoiceCard({
  icon,
  title,
  body,
  badge,
  onClick,
}: Readonly<{ icon: React.ReactNode; title: string; body: string; badge?: string; onClick: () => void }>) {
  return (
    <Card onClick={onClick} size="md" aria-label={title}>
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2">
          <IconTile variant="brand">{icon}</IconTile>
          {badge && <Badge variant="brand">{badge}</Badge>}
        </div>
        <Text variant="h3">{title}</Text>
        <Text variant="body-sm" tone="secondary">
          {body}
        </Text>
      </div>
    </Card>
  );
}
